import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';

const BoardList = () => {
  const location = useLocation();
  const params = useParams();
  const cate = params.cate || location.state?.cate || '';
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [select, setSelect] = useState(0);
  const [tab, setTab] = useState(1);
  const [menuData, setMenuData] = useState([]);
  const [allData, setAllData] = useState('');
  const navigate = useNavigate();
  const { decodeS1 } = useAuth();
  const pageSize = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOption, setSearchOption] = useState('제목');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!cate) return;
    getBoard();
  }, [cate, page]);

  const getBoard = () => {
    if (!cate) return;
    axios
      .get(`https://ciuc.or.kr:8443/api/get/board_list?cate=${cate}&page=${page}&pageSize=${pageSize}`)
      .then((response) => {
        const formattedData = response.data.data.map((item) => ({
          ...item,
          date: formatDate(item.date),
        }));
        setMenuData(formattedData);
        setAllData(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleSelect = (num) => {
    if (select === num) {
      setSelect(0);
    } else {
      setSelect(num);
    }
  };

  useEffect(() => {
    if (cate !== '') {
      switch (cate) {
        case 'notice':
          setTitle('공지사항');
          break;
        case 'business':
          setTitle('사업공고');
          break;
        case 'education':
          setTitle('교육공고');
          break;
        case 'employment':
          setTitle('채용공고');
          break;
        case 'bid':
          setTitle('입찰공고');
          break;
        case 'news':
          setTitle('보도자료');
          break;
        case 'reference':
          setTitle('자료실');
          break;
        case 'archive':
          setTitle('융합원 아카이브');
          break;
        default:
          return;
      }
    }
  }, [cate]);

  const handleSearch = useCallback(async () => {
    try {
      const url = `https://ciuc.or.kr:8443/api/get/board_search?page=${currentPage}&searchTerm=${searchTerm}&searchOption=${searchOption}&pageSize=${pageSize}&category=${cate}`;
      const res = await axios.get(url);
      const { totalItems, results } = res.data;

      const searchTotal = Math.ceil(totalItems / pageSize);

      setTotalPages(searchTotal);
      setMenuData(results);
      setCurrentPage(1);
    } catch (error) {
      console.error('검색 결과 가져오기 오류:', error);
    }
  }, [searchTerm, searchOption, currentPage, pageSize, cate]);

  const handleReset = useCallback(() => {
    setSearchTerm('');
    setSearchOption('제목');
    setCurrentPage(1);
    getBoard();
  }, []);

  const hitCount = async (idx) => {
    try {
      await axios.post(`https://ciuc.or.kr:8443/api/post/board/hit_count/${idx}`);
    } catch (err) {
      console.log(err);
    }
  };

  const handleRowClick = (idx) => {
    // 현재 페이지 리스트에서 선택한 글 찾기
    const selectedIndex = menuData.findIndex((item) => item.idx === idx);
    const selectedItem = menuData[selectedIndex];

    if (selectedItem) {
      hitCount(idx);
      navigate(`/board/${cate}/${idx}`, {
        state: {
          menuData: selectedItem, // 현재 글
          cate: cate,
          list: menuData, // 🔹 현재 페이지의 전체 리스트 함께 전달
        },
      });
    }
  };

  const moveBoard = (cate) => {
    navigate(`/board/${cate}`, { state: { cate: cate } });
  };

  const movePage = (path) => {
    navigate(path);
  };

  const handleWrite = () => {
    navigate(`/board/${cate}/write`);
  };

  // 🔹 총 페이지 수(텍스트/페이징 공용)
  const totalPageCount = allData ? Math.ceil(allData / pageSize) : 1;

  const renderPagination = () => {
    const totalPages = totalPageCount;
    if (totalPages <= 1) return null;

    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const goToPage = (target) => {
      if (target < 1 || target > totalPages || target === page) return;
      setPage(target);
    };

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          type="button"
          className={`page_btn page_btn--number ${i === page ? 'is-active' : ''}`}
          onClick={() => goToPage(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <>
        <button
          type="button"
          className={`page_btn page_btn--arrow ${page === 1 ? 'is-disabled' : ''}`}
          onClick={() => goToPage(1)}
        >
          «
        </button>
        <button
          type="button"
          className={`page_btn page_btn--arrow ${page === 1 ? 'is-disabled' : ''}`}
          onClick={() => goToPage(page - 1)}
        >
          ‹
        </button>

        {pageNumbers}

        <button
          type="button"
          className={`page_btn page_btn--arrow ${page === totalPages ? 'is-disabled' : ''}`}
          onClick={() => goToPage(page + 1)}
        >
          ›
        </button>
        <button
          type="button"
          className={`page_btn page_btn--arrow ${page === totalPages ? 'is-disabled' : ''}`}
          onClick={() => goToPage(totalPages)}
        >
          »
        </button>
      </>
    );
  };

  return (
    <div className="sub_wrap">
      <div className="sub_back">
        {/* 상단 배너 */}
        <div className="sub_banner_area notice">
          <div className="sub_banner_back">
            <div className="navi_text">
              <div className="home_icon_sub"></div>
              {'> '}알림 및 소식{' > '}
              {title}
            </div>
            <div className="banner_main_text">{title}</div>
          </div>
        </div>

        {/* 상단 네비 (드롭다운 2개) */}
        <div className="navi_area">
          <div className="navi_back_wrap">
            <div className="navi_back">
              {/* 1. 대분류 셀렉트 */}
              <div className="navi_box" onClick={() => handleSelect(1)}>
                <div className="navi_main_text">알림 및 소식</div>
                <div className="navi_arrow"></div>
                <div className={`navi_select_box ${select === 1 && 'active'}`}>
                  <div className="select_row" onClick={() => moveBoard('notice')}>
                    알림 및 소식
                  </div>
                  <div className="select_row" onClick={() => movePage('/fusionSupportProgram')}>
                    사업분야
                  </div>
                  <div className="select_row" onClick={() => movePage('/inquiryinfo')}>
                    기업연구동
                  </div>
                  <div className="select_row" onClick={() => movePage('/meetingroom')}>
                    보유시설
                  </div>
                  <div className="select_row" onClick={() => movePage('/vision')}>
                    융합원소개
                  </div>
                </div>
              </div>

              {/* 2. 소분류 셀렉트 */}
              <div className="navi_box" onClick={() => handleSelect(2)}>
                <div className="navi_main_text">{title}</div>
                <div className="navi_arrow"></div>
                <div className={`navi_select_box ${select === 2 && 'active'}`}>
                  <div className="select_row" onClick={() => moveBoard('notice')}>
                    공지사항
                  </div>
                  <div className="select_row" onClick={() => moveBoard('business')}>
                    사업공고
                  </div>
                  <div className="select_row" onClick={() => moveBoard('education')}>
                    교육공고
                  </div>
                  <div className="select_row" onClick={() => moveBoard('employment')}>
                    채용공고
                  </div>
                  <div className="select_row" onClick={() => moveBoard('bid')}>
                    입찰공고
                  </div>
                  <div className="select_row" onClick={() => moveBoard('news')}>
                    보도자료
                  </div>
                  <div className="select_row" onClick={() => moveBoard('reference')}>
                    자료실
                  </div>
                  <div className="select_row" onClick={() => moveBoard('archive')}>
                    융합원 아카이브
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 본문 영역 */}
        <div className="sub_main_area board">
          {/* 고정 사이드바 */}
          <aside className="intro_aside_fixed">
            <div className="intro_aside_header">
              <div className="intro_aside_header">
                <div className="center_box">
                  <span>알림 및 소식</span>
                  <div className="center_line"></div>
                </div>
              </div>
            </div>
            <ul className="intro_aside_list">
              <li className={`intro_aside_item ${isActive('/board/notice')}`} onClick={() => moveBoard('notice')}>
                <span>공지사항</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/business')}`} onClick={() => moveBoard('business')}>
                <span>사업공고</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/education')}`} onClick={() => moveBoard('education')}>
                <span>교육공고</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li
                className={`intro_aside_item ${isActive('/board/employment')}`}
                onClick={() => moveBoard('employment')}
              >
                <span>채용공고</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/bid')}`} onClick={() => moveBoard('bid')}>
                <span>입찰공고</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/news')}`} onClick={() => moveBoard('news')}>
                <span>보도자료</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/reference')}`} onClick={() => moveBoard('reference')}>
                <span>자료실</span>
                <span className="aside_arrow">&gt;</span>
              </li>
              <li className={`intro_aside_item ${isActive('/board/archive')}`} onClick={() => moveBoard('archive')}>
                <span>융합원 아카이브</span>
                <span className="aside_arrow">&gt;</span>
              </li>
            </ul>
          </aside>

          <div className="board_container-sub">
            {/* 제목 + 현재 경로 */}
            <div className="board_path_box">
              <div className="path_title">{title}</div>
              <div className="path_navi">
                <span className="home_icon_small">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path d="M4 11.5L12 4l8 7.5V20a1 1 0 0 1-1 1h-5v-5h-4v5H5a1 1 0 0 1-1-1v-8.5z" fill="#999999" />
                    <path
                      d="M2.5 12L12 3l9.5 9"
                      fill="none"
                      stroke="#cccccc"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="path_arrow">&gt;</span>
                <span>알림 및 소식</span>
                <span className="path_arrow">&gt;</span>
                <span className="current">{title}</span>
              </div>
            </div>

            <div className="list_area">
              {/* 상단 : 총 게시글 수 + 검색 */}
              <div className="list_top">
                <div className="board_info">
                  <div className="board_total_count">
                    총{' '}
                    <span className="count" style={{ letterSpacing: '1.3px' }}>
                      {allData}
                    </span>
                    건
                    <span className="page_info">
                      {' '}
                      ({page}/{totalPageCount} 페이지)
                    </span>
                  </div>
                </div>

                <div className="search_box">
                  <select value={searchOption} onChange={(e) => setSearchOption(e.target.value)}>
                    <option>제목</option>
                    <option>내용</option>
                    <option>작성자</option>
                  </select>

                  <div className="search_input_wrap">
                    <span className="search_icon" />
                    <input
                      className="search_input"
                      placeholder="검색어를 입력하세요"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="option-box">
                    <button type="button" className="search_btn" onClick={handleSearch}>
                      검색
                    </button>
                    <button type="button" className="reset-btn" onClick={handleReset}>
                      초기화
                    </button>
                  </div>
                </div>
              </div>

              {/* 글쓰기 버튼 */}
              <div className="write_box">
                {decodeS1() === 'admin' && (
                  <div className="write_btn" onClick={handleWrite}>
                    {title} 등록
                  </div>
                )}
              </div>

              {/* 리스트 */}
              <div className="list_box">
                {isMobile ? (
                  menuData.length > 0 ? (
                    menuData.map((item, index) => (
                      <div className="board-mobile" key={index} onClick={() => handleRowClick(item.idx)}>
                        <div className="mb-wrap">
                          <div className="mobile-sub">{item.title}</div>
                          <div className="mobile-btm">
                            <span>{item.writer}&nbsp;|&nbsp;</span>
                            <span>{item.date}&nbsp;|&nbsp;</span>
                            <span>조회수: {item.hit}</span>
                            {item.img1 == null ? '' : <span className="clip_icon"></span>}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no_data_mobile">
                      <div className="no-mobile-empty">
                        <div className="no-mobile-empty-icon">🔍</div>
                        <div className="no-mobile-empty-title">데이터가 존재하지 않습니다.</div>
                        <div className="no-mobile-empty-desc">
                          검색 조건을 다시 확인하거나 ‘초기화’ 버튼을 눌러 전체 목록을 확인해 주세요.
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <table className="board_table">
                    <thead className="table_head">
                      <tr className="head_row">
                        <th className="head_section num">번호</th>
                        <th className="head_section title">제목</th>
                        <th className="head_section date">작성자</th>
                        <th className="head_section date">등록일</th>
                        <th className="head_section hit">조회수</th>
                      </tr>
                    </thead>
                    <tbody className="table_body">
                      {menuData.length === 0 ? (
                        <tr className="body_row no_table_data_row">
                          <td className="no_table_data" colSpan="5">
                            <div className="no-table-empty">
                              <div className="no-table-empty-icon">🔍</div>
                              <div className="no-table-empty-title">데이터가 존재하지 않습니다.</div>
                              <div className="no-table-empty-desc">
                                검색 조건을 다시 확인하거나 ‘초기화’ 버튼을 눌러 전체 목록을 확인해 주세요.
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        menuData.map((item, index) => {
                          const itemNumber = allData - ((page - 1) * pageSize + index);
                          return (
                            <tr className="body_row" key={index} onClick={() => handleRowClick(item.idx)}>
                              <td className="body_section num">{itemNumber}</td>
                              <td className="body_section title">{item.title}</td>
                              <td className="body_section date">{item.writer}</td>
                              <td className="body_section date">{formatDate(item.date)}</td>
                              <td className="body_section hit">{item.hit}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* 페이징 */}
              <div className="pagination_box">{renderPagination()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardList;
